/**
 * The boat catalogue: the presets a captain can buy a whole ship as, the cannon types that can
 * be bolted fore and aft of one, how a ship grows once it is in the water, and the score
 * thresholds that growth happens at.
 *
 * Like [Settings.ts](Settings.ts) this file holds the **schema only** — no values are read
 * from disk here, so it imports nothing and compiles for the browser, for Node, and for the
 * editor alike. The stored tree is `config/boat.json`, written by the Boat panel of the
 * dashboard (`editor/panels/BoatPanel.tsx`).
 *
 * It is a sibling of `Settings.ts` rather than another panel inside it because it is not a
 * panel of dials. Every other setting is one finite number with a slider; this is a list of
 * boats, each with a sprite frame and a set of slot points, and `SettingField` cannot describe
 * it. The two files share nothing but the habit of coercing on the way in.
 *
 * **It used to be three ladders** — hulls, guns and sails, each climbed by spending an upgrade
 * point mid-battle. It is a catalogue now: a preset and two cannon types are **bought ashore**
 * (design/Shop.md) and everything that happens after the ship is in the water is automatic,
 * driven by one level off one curve (design/Evolution.md). The two shapes that survived the
 * change are the ones that were never about choosing — the milestone curve, and the rule that
 * a gun's numbers are authored as a base plus what a level is *worth* rather than as a table of
 * absolutes.
 *
 * Every `frame` in here is an **art reference**: the name of a frame in one of the game's
 * sheets, or the name of an **animation** in one — `sails/plain_small_sail` is three frames of
 * billow. Whoever draws it looks the name up in the animations first and falls back to a single
 * frame, so an entry stores the same kind of string whether its art moves or not.
 *
 * **Nothing here has a display name.** An entry is identified by its index — preset 1, cannon
 * type 3 — and the words a player reads come from the localization system, which is the only
 * place that can hold the same label in eight languages. A name stored here would be an English
 * string masquerading as data, and a second source of truth the moment translation lands.
 *
 * See design/Evolution.md for what growth means, design/Shop.md for what is bought, and
 * design/Settings.md for the storage contract — that document and this file are a contract.
 */

/**
 * Where one thing is bolted to a hull, in **hull-local** coordinates.
 *
 * Both axes are fractions of the hull's own half-size, so a slot stays put when the hull's
 * `length` changes — and, now that a hull also grows during a life, when its **scale** changes.
 * `x` runs across the beam from -1 at the port rail to +1 at starboard, and `y` runs along the
 * keel from -1 at the bow to +1 at the stern. Deliberately not pixels: a designer who lengthens
 * a boat should not have to re-place its guns, and a boat that doubles in size mid-battle must
 * not shed them.
 */
export interface Slot {
    /** Across the beam. -1 is the port rail, 0 the keel, +1 starboard. */
    x: number;
    /** Along the keel. -1 is the bow, 0 amidships, +1 the stern. */
    y: number;
    /**
     * Which way the fitting points, in degrees, 0 being at the bow — the art faces north,
     * so this is the same zero the hull's heading uses. A port gun is -90.
     */
    rotation: number;
    /** Multiple of the fitting's own on-screen size. 1 draws it at the entry's `size`. */
    scale: number;
}

/**
 * One sprite laid on a hull: a wheel, a hatch, a crate, a coil of rope. Decoration and
 * silhouette, never a fitting anything fills.
 *
 * A preset is a **base frame plus parts**, not a single sprite, because the sheet is drawn
 * that way: `boats.png` has a handful of bare planked hulls and three dozen pieces of deck
 * furniture to put on them. Composing them in the dashboard is the difference between three
 * boats and three boats that look like different ships.
 *
 * Position is the same hull-local space a `Slot` uses, so the two can be placed by the same
 * click. What a part has instead of a `scale` is a `size` in pixels: nothing else decides how
 * big a wheel is, whereas a gun is sized by its type and only nudged by the slot.
 */
export interface HullPart {
    /** Frame in the `boats` sheet, e.g. `boats/wheel`. */
    frame: string;
    /** Across the beam. -1 is the port rail, 0 the keel, +1 starboard. */
    x: number;
    /** Along the keel. -1 is the bow, 0 amidships, +1 the stern. */
    y: number;
    /** Which way it faces, in degrees, 0 being at the bow. */
    rotation: number;
    /** On-screen length in pixels, taken from the frame's untrimmed height like every other size. */
    size: number;
}

/**
 * One sail on a preset's rig: what it looks like and what it is worth.
 *
 * **A sail is authored with the boat, not bought.** Canvas used to be a ladder a point could
 * climb; a sloop's rig is not an accessory somebody fitted to a sloop, it is part of what the
 * boat *is*, and the shop sells boats (design/Evolution.md). So a rig is a list of these, one
 * per sail slot on the preset that carries it.
 *
 * A sail's art is an **animation** — three frames of the canvas bellying in and out — and the
 * frames are different heights because the spar stays put while the billow grows off it. Two
 * rules follow for anything drawing one, and they are in `editor/atlas.ts` as well as here:
 * the whole animation is scaled off its **first** frame, and it is pinned by its **bottom**
 * edge, which is where the artist put the spar.
 */
export interface SailFitting {
    /** Art in the `sails` sheet — an animation name, e.g. `sails/plain_small_sail`. */
    frame: string;
    /**
     * On-screen depth of this sail at full billow, in pixels, before its slot's own `scale` —
     * measured on the first frame, bow-ward off the spar.
     *
     * The **width** follows from the art's aspect, about 2.5x this, and the width is what a
     * designer is actually judging: a yard should reach roughly across the beam.
     */
    size: number;
    /**
     * How much of the wind **this one sail** takes, as a multiple. This is what canvas is for.
     *
     * It is a share of the wind rather than a flat speed bonus, so a rig widens the gap between
     * sailing well and sailing badly rather than making a boat quicker everywhere: a full rig
     * running with the weather is far faster than a bare one, and in irons the two are the same
     * boat. How several combine is `rigDrive` in [Fitout.ts](Fitout.ts) — each further sail
     * counts half as much as the one before, best first.
     */
    speed: number;
}

/**
 * One boat a captain can buy and go to sea in: its hull, how it handles, where its two guns are
 * bolted, the canvas it carries, and what it costs.
 *
 * **This is the whole ship, not a rung.** It used to be `HullLevel`, one step of a ladder a
 * point climbed mid-battle; a preset is a thing bought ashore and sailed for a whole life, and
 * nothing during that life changes any number on it. What growth changes is the **scale** it is
 * drawn at (see `ScaleCurve`) and the guns bolted to it (see `CannonType`).
 *
 * The slots belong to the **preset** rather than to the gun or the sail, which is the one
 * decision in this file that shapes how a boat is drawn: a slot is where a fitting rests and
 * the centre of the arc a gun swings through, and it is a property of the deck plan.
 */
export interface BoatPreset {
    /** Frame in the `boats` sheet that draws the hull, e.g. `boats/hulls/hull_09`. */
    frame: string;
    /**
     * On-screen length, bow to stern, in pixels, **at the bottom of the scale curve**. What a
     * boat actually measures in the water is this times `scaleAt(level)`.
     */
    length: number;
    /**
     * Cruise speed as a multiple of `boatBaseSpeed`. **Fixed for a whole life** — growing does
     * not slow a boat down, and a captain who bought the quick one is quick at every level.
     * That is what makes the preset a decision rather than a starting position.
     */
    speed: number;
    /** Turn rate as a multiple of `TURN_RATE`. Fixed for a whole life, for the same reason. */
    turn: number;
    /**
     * How far the battle camera sits out while sailing this boat **at the bottom of the scale
     * curve**: the scale the world is drawn at, so below 1 is a wider view of the sea and
     * everything in it drawn smaller.
     *
     * It is on the preset rather than being one number for the game because boats differ in
     * size by a factor of two before anybody has levelled at all. As a captain grows the battle
     * divides this by the current scale, so the player's own ship stays about the same size on
     * screen and it is the *sea* that gets smaller — the same rule the hull ladder used to
     * follow, now continuous. `defaultCameraZoom` is what a preset added in the dashboard
     * arrives holding.
     *
     * Purely a view: no other client sees it, the server never reads it, and nothing about the
     * simulation changes with it. See design/Ships.md.
     */
    cameraZoom: number;
    /**
     * What this boat costs, in doubloons. **Preset 0 costs nothing and is always owned** — an
     * .io arena where the free boat is food has already lost the player it needed to keep.
     *
     * A price is not a power level. design/Shop.md asks for the spread to be a spread of
     * *character* — quick and fragile against slow and long-reaching — rather than a ladder, and
     * the dashboard's derived column exists to show whether it actually is.
     */
    cost: number;
    /**
     * Sprites laid on the hull, drawn **in array order** over the base frame and under the
     * guns. Decoration: nothing here is a fitting and nothing here is networked.
     */
    parts: HullPart[];
    /**
     * Where the two guns are bolted — **exactly `CANNON_SLOTS` of them, bow first, then stern**.
     * The coercion pads and trims to that count, so every downstream reader (the roster, the
     * header's battery strip, the wire array) can count on two.
     */
    cannonSlots: Slot[];
    /** Where this boat's canvas sits. One sail of `rig` per slot, in order. */
    sailSlots: Slot[];
    /**
     * The canvas this boat carries, one entry per sail slot. A rig longer than the slot list is
     * trimmed by whatever draws it; a shorter one leaves the last spars bare.
     */
    rig: SailFitting[];
}

/**
 * What a gun *does*, resolved: the five numbers the simulation actually fires with.
 *
 * They are never authored as a set for a particular level. A `CannonType` holds them for a gun
 * at level 1 (`base`), holds what one level is worth (`perLevel`), and `cannonStatsAt` puts the
 * two together. This is the resolved end of that, and the only shape anything outside the tree
 * — a roster, a shot, the dashboard's derived column — holds.
 */
export interface CannonStats {
    /**
     * What fraction of the target's hold a hit takes, 0 to 1. Used to be `HIT_STEAL_FRACTION`,
     * one global 0.4 in `shared/Battle.ts`.
     *
     * It lives on the gun because the gun is the thing the shop sells, and the thing a shop
     * sells has to be the thing whose number moves (design/Battle.md). The tuning hazard that
     * comes with it: the steal is what makes a chase *end*, so a cheap gun must be a worse gun
     * rather than a gun that cannot finish a fight.
     */
    steal: number;
    /** How far a shot carries before it falls into the sea, in world pixels. */
    range: number;
    /** How fast the ball leaves the muzzle, in world pixels per second. */
    projectileSpeed: number;
    /** How long one gun waits between shots, in milliseconds. */
    cooldown: number;
    /** How fast one gun swings on its mount, in degrees per second. */
    rotationSpeed: number;
}

/**
 * What **one level** adds to a gun. Every field points the same way — bigger is stronger — which
 * is why the reload is stored as a drop rather than as a negative step.
 */
export interface CannonStep {
    /** How much more of a hold each level takes, as a fraction. */
    stealStep: number;
    /** How much **further** each level carries, in world pixels. */
    rangeStep: number;
    /** How much **faster** the ball leaves, in world pixels per second. */
    projectileSpeedStep: number;
    /**
     * How many **milliseconds each level takes off** the reload.
     *
     * The one field here whose sign is flipped, and deliberately. Every other increment points
     * the same way as the thing it improves, and a gun gets better by reloading *faster*;
     * storing the drop means a designer types 20 for "a fiftieth of a second quicker" rather
     * than -20, and every box on the page reads the same way round. `cannonStatsAt` is the one
     * place that subtracts it.
     *
     * Milliseconds rather than the seconds the rest of the code runs on, because this is a
     * reload a designer types — 1000, 750, 2500 — and a `0.75` in a box invites a `0.075`.
     * Whatever fires the gun converts once, at the edge.
     */
    cooldownDrop: number;
    /**
     * How much **faster** the gun swings on its mount per level, in degrees per second.
     *
     * Degrees rather than the radians the simulation runs on, for the same reason every other
     * angle in the tree is degrees: a slot's `rotation` is typed as -90 for a port gun, and a
     * traverse rate has to be judged against those same numbers.
     */
    rotationSpeedStep: number;
}

/**
 * One kind of gun a captain can buy: what it is, what it costs, what it does at level 1, and
 * what each level after that is worth.
 *
 * **A type is not a rung.** It used to be `CannonLevel`, one step of a ladder a point climbed;
 * a type is a thing bought ashore, and the bow gun and the stern gun may be different types.
 * What climbs during a life is the captain's **level**, and both guns climb it together
 * (design/Evolution.md).
 *
 * **The numbers are authored as a base plus what a level is worth**, which is the one idiom
 * carried over wholesale from the ladder it replaced, and for the same reason. A designer
 * tuning this is deciding *how much better an upgrade makes you*; with a table of absolutes
 * that decision is never written down anywhere — it lives in the differences between rows — and
 * lengthening the starting reach means retyping every row above it.
 *
 * What changed is that the increments are a **formula** rather than a list. The milestone curve
 * has no last rung (`milestoneScore`), so a captain still climbing at level 90 has to have an
 * answer, and a stored array can only answer for as many rows as somebody typed.
 */
export interface CannonType {
    /** Art in the `boats` sheet for one gun, e.g. `boats/cannon`. */
    frame: string;
    /** On-screen length of one gun, in pixels, before a slot's own `scale`. */
    size: number;
    /**
     * What this gun costs, in doubloons. **Type 0 costs nothing and is always owned** — a boat
     * with no gun cannot join the only fight the game has.
     */
    cost: number;
    /** What this gun is at level 1: the numbers a fresh captain fires with. */
    base: CannonStats;
    /** What each level after the first adds. See `diminish`. */
    perLevel: CannonStep;
    /**
     * How much of the previous level's step each further level is worth. 1 is a straight line;
     * below 1 is a climb that flattens out.
     *
     * It exists because the milestone ladder is endless and a linear climb over it is not: at
     * `diminish` 1 a captain deep enough into a run reaches whatever `BOAT_LIMITS` allows and
     * stops there, which makes the ceiling do all the shaping. Below 1 the ladder converges on
     * its own — `base + step / (1 - diminish)` — so the gun a designer authored has a stated
     * top rather than an accidental one. `BOAT_LIMITS` still clamps every total regardless.
     */
    diminish: number;
    /**
     * Where the ball actually leaves this gun, in the **gun's own** space — across the barrel
     * and along it, as fractions of the gun's drawn length. `0, 0` is the slot itself.
     *
     * The axes are the ship's, turned with the gun: `muzzleY` runs along the barrel with
     * **negative toward the muzzle**, the same sign the hull's `y` uses for the bow, and
     * `muzzleX` runs across it, positive to the gun's own starboard. So a gun whose art fills
     * its box spits its ball from about `y = -0.5`, whichever rail it is bolted to and whichever
     * way the ship happens to be pointing.
     *
     * The unit is the gun's **drawn** length — `size` times the slot's `scale` — rather than
     * pixels, so one spawn point authored against the art holds in either slot and at any size:
     * a gun scaled up carries its muzzle out with it. It is deliberately not a fraction of the
     * sprite's untrimmed box, because `shared/` never sees a texture and could not resolve one.
     *
     * It defaults to `0, 0`, which is what every gun did before the field existed: the ball
     * starts at the slot. That is a decision rather than an unset value — a ball spawned a long
     * way ahead of the rail is already inside a ship that is alongside — so moving it out is an
     * authoring act, done by dragging the gizmo on the Boat panel's cannon page.
     */
    muzzleX: number;
    muzzleY: number;
}

/**
 * How big a boat is at a given level: a floor, a ceiling, how many levels it takes to get from
 * one to the other, and the shape of the approach.
 *
 * ```
 * t     = clamp(level / max(1, levels), 0, 1)
 * scale = min + (max - min) * t ^ curve
 * ```
 *
 * **Bounded by construction**, which is the point of storing a ceiling rather than a growth
 * rate. A boat that grew without bound would be a whale that fills its own screen with the sea
 * gone from behind it, and no amount of care in the camera can fix a hull wider than the window.
 *
 * A level is `Ship.level` — milestones crossed this life — so level 0 is a fresh captain and
 * sits exactly at `min`. Everything the scale moves is listed in design/Evolution.md; the short
 * version is that it moves how big the boat is, how far it is hit from and how far back the
 * camera sits, and it moves **nothing about how the boat handles or how far its guns reach**.
 */
export interface ScaleCurve {
    /** The size a fresh captain spawns at. The arena's own unit is preset 0 at this scale. */
    min: number;
    /** The ceiling. Reached at `levels` and held for every level above it. */
    max: number;
    /** How many levels it takes to reach the ceiling. */
    levels: number;
    /** Exponent shaping the approach. 1 is linear; below 1 grows fast early and eases in. */
    curve: number;
}

/**
 * What a gun of `type` fires with at `level`: its base, plus `level - 1` diminishing steps.
 *
 * The **only** way to turn a stored type into numbers a gun can be fired with. Nothing else
 * should sum `rangeStep` by hand — the arena, the client and the dashboard all come through
 * here, for the same reason both ends resolve a fitout from the same file: two copies of this
 * arithmetic is two guns that can disagree about how far a ball goes, and design/Networking.md
 * says a ball is at the same place on both screens.
 *
 * `level` is 1-based and generous about what it is handed. Zero or less is the bare base, and
 * there is **no top** — ask for level 900 and there is an answer, which is exactly what an
 * endless milestone ladder requires.
 *
 * Every total is clamped into the same `BOAT_LIMITS` a base is authored against, which is where
 * a ladder of decrements stops. The floor on `cooldown` is the one that matters: it is a
 * divisor of nothing but it is a reload, and a gun at 0ms is a gun that fires every frame.
 */
export function cannonStatsAt(type: CannonType, level: number): CannonStats {
    const steps = levelSteps(Math.floor(level), type.diminish);
    const { base, perLevel } = type;

    return {
        steal: num(base.steal + perLevel.stealStep * steps, BOAT_LIMITS.steal, BOAT_LIMITS.steal.min),
        range: num(base.range + perLevel.rangeStep * steps, BOAT_LIMITS.range, BOAT_LIMITS.range.min),
        projectileSpeed: num(
            base.projectileSpeed + perLevel.projectileSpeedStep * steps,
            BOAT_LIMITS.projectileSpeed,
            BOAT_LIMITS.projectileSpeed.min
        ),
        // The one subtraction in the formula. See `CannonStep.cooldownDrop`.
        cooldown: Math.round(num(base.cooldown - perLevel.cooldownDrop * steps, BOAT_LIMITS.cooldown, BOAT_LIMITS.cooldown.min)),
        rotationSpeed: num(
            base.rotationSpeed + perLevel.rotationSpeedStep * steps,
            BOAT_LIMITS.rotationSpeed,
            BOAT_LIMITS.rotationSpeed.min
        )
    };
}

/**
 * How many whole steps' worth of increment a gun at `level` has accumulated:
 * `1 + d + d² + ... + d^(level-2)`, the geometric sum that `diminish` describes.
 *
 * Level 1 is zero steps — it *is* the base, by construction — and the sum converges to
 * `1 / (1 - d)` for `d < 1`, which is what gives an endless ladder a stated top.
 */
function levelSteps(level: number, diminish: number): number {
    const climbed = Math.max(0, level - 1);
    if (climbed === 0) return 0;

    const d = num(diminish, BOAT_LIMITS.diminish, 1);
    if (Math.abs(d - 1) < 1e-9) return climbed;

    return (1 - Math.pow(d, climbed)) / (1 - d);
}

/**
 * How big a boat is at `level`, as a multiple of its preset's authored length. See `ScaleCurve`.
 *
 * Clamped at both ends, so a level past the ceiling is the ceiling and a negative one is the
 * floor. Like `cannonStatsAt` it is the **only** way to answer the question: the client draws
 * the boat this size and the server measures a hit against it, and the two must not each do
 * their own arithmetic.
 */
export function scaleAt(curve: ScaleCurve, level: number): number {
    const span = Math.max(1, Math.floor(curve.levels));
    const t = Math.min(1, Math.max(0, Math.floor(level) / span));

    return curve.min + (curve.max - curve.min) * Math.pow(t, curve.curve);
}

/**
 * The milestone ladder, as a **formula rather than a list**. Untouched by the move to a shop:
 * it was never the part of the tree that asked the player anything.
 *
 * A ship's growth has no top rung. A captain still sailing at fifty thousand points is still
 * growing, and a stored array can only answer that for as many rows as somebody typed — so the
 * ladder is generated, level 1, 2, 3 and on, from four numbers. The shape design/Evolution.md
 * asks for ("the gaps widen as they go") becomes the formula's own behaviour instead of
 * something a designer has to keep true by hand across eight boxes.
 *
 * The curve is a **first threshold plus a geometric run of gaps**:
 *
 * ```
 * score(1) = base
 * score(n) = base + step * (growth^(n-1) - 1) / (growth - 1)   // and base + step * (n-1) at growth 1
 * ```
 *
 * which says: the first gap is `step`, and every gap after it is `growth` times the one before.
 * That is the parameterisation a designer can hold in their head — *first level at 200, then
 * 150 more, and each one after that is 28% further than the last* — where the bare exponential
 * `base * growth^n` makes the early rungs a consequence of the late ones and moves all of them
 * whenever the curvature is touched.
 *
 * **Nothing needs the inverse.** A ship carries the level it has reached and compares its score
 * against `milestoneScore(curve, level + 1)`; no code has to ask which level an arbitrary score
 * sits on, which is what keeps an unbounded ladder as cheap to run as a stored array was.
 */
export interface MilestoneCurve {
    /** Score at which the first level is reached. Level 1 lands exactly here. */
    base: number;
    /** Points from the first threshold to the second — the gap before any growth is applied. */
    step: number;
    /** How much wider each gap is than the one before it. 1 is a straight ladder. */
    growth: number;
    /** Thresholds are rounded to a multiple of this, so the ladder reads in round numbers. */
    round: number;
}

/** Nearest multiple of `to`, which is never zero — `BOAT_LIMITS.milestoneRound.min` is 1. */
function roundTo(value: number, to: number): number {
    return Math.round(value / to) * to;
}

/**
 * The score at which `level` is reached, counting from 1. There is **no cap**: ask for level 400
 * and there is an answer.
 *
 * Rounding is applied to the *distance from* `base`, never to the whole threshold, so level 1
 * is exactly the number that was typed into the box. `base` is a decision — the score a fresh
 * captain is first rewarded at — and a `round` of 50 quietly pulling 210 down to 200 would be
 * one dial editing another.
 *
 * The `Math.max` is the only guard. `round` can be set larger than `step`, which would land
 * two neighbouring levels on the same score and grant two at once; the floor keeps every level
 * at least one `round` above the one below it, so the ladder is strictly increasing whatever
 * the four numbers say. With a sane `step >= round` it never binds.
 */
export function milestoneScore(curve: MilestoneCurve, level: number): number {
    const n = Math.max(1, Math.floor(level));
    const base = Math.round(curve.base);
    const span = Math.abs(curve.growth - 1) < 1e-9 ? n - 1 : (Math.pow(curve.growth, n - 1) - 1) / (curve.growth - 1);
    return Math.max(base + roundTo(curve.step * span, curve.round), base + (n - 1) * curve.round);
}

/**
 * Most levels one life may climb. **Not a design ceiling** — design/Settings.md means what it
 * says, the curve has no last entry and a run is not meant to hit this. It is two safety
 * bounds at once:
 *
 * - the server walks the ladder in a `while` loop when a score jumps (a fat plunder can cross
 *   several levels at once), and a curve authored flat — `growth` 1, `base` and `step` at their
 *   floor of 10 — puts four hundred million levels under a `uint32` score. The loop is bounded
 *   rather than the dials being narrowed, because the dials are the designer's;
 * - the level is a `uint16` on the wire, and this sits well under it.
 *
 * At the shipped curve, level 250 is a score no arena will produce. A captain who somehow
 * reaches it stops growing, which is the right failure: nothing breaks, and the progress bar
 * simply stays full.
 */
export const MILESTONE_LEVEL_MAX = 250;

/**
 * How far a score has come from the level it last reached toward the next one, 0 to 1 — the
 * progress bar in the corner of a battle (design/UI.md).
 *
 * Measured from the **previous threshold**, not from zero, so the bar means the same thing at
 * every level: a run whose gaps widen would otherwise show a bar that fills more and more
 * slowly and tells the player nothing. Below the first level the floor is zero, which is why a
 * fresh captain on STARTING_SCORE opens a battle with the bar already part-filled — they are
 * genuinely part of the way there.
 *
 * Clamped at both ends: a score can fall below the level it already reached (design/Battle.md
 * keeps the growth and lets the score go), and a captain at MILESTONE_LEVEL_MAX has no next
 * level to be short of.
 */
export function milestoneProgress(curve: MilestoneCurve, level: number, score: number): number {
    if (level >= MILESTONE_LEVEL_MAX) return 1;

    const from = level < 1 ? 0 : milestoneScore(curve, level);
    const to = milestoneScore(curve, level + 1);
    if (to <= from) return 1;

    return Math.min(1, Math.max(0, (score - from) / (to - from)));
}

/** The first `count` thresholds, for anything drawing the ladder rather than asking it a question. */
export function milestoneLadder(curve: MilestoneCurve, count: number): number[] {
    const ladder: number[] = [];
    for (let level = 1; level <= count; level++) ladder.push(milestoneScore(curve, level));
    return ladder;
}

/**
 * A device on the sail, and where it sits.
 *
 * `width`, `cx` and `cy` are fractions of the **sail's first frame's box**, not of the device's
 * own art, which is what lets one entry place the same emblem on every rig without a table per
 * preset. They are measured by `npm run import-boat` off the drawing the device was lifted from
 * and written into the emblems sheet's `placements`; the numbers here are seeded from that and
 * tunable by hand after. See design/Art.md §7 — the device's *centre*
 * transfers between rigs to within 2% of the box, its *size* only to within 11%, so this is the
 * one place a device that reads small on a big sail is fixed.
 *
 * Entry 0 is **the device that is no device**: an empty `frame`, so `emblem: 0` needs no special
 * case anywhere and a client a version behind draws a stranger's unknown emblem as none rather
 * than as nothing.
 */
export interface EmblemFitting {
    /** Art reference into the `emblems` sheet. Empty means no device. */
    frame: string;
    /** How wide to draw it, as a fraction of the sail's box. */
    width: number;
    /** Where its centre goes, as fractions of the sail's box: 0 to 1 across, 0 to 1 down. */
    cx: number;
    cy: number;
}

/**
 * What a captain chooses about how their ship *looks*, as opposed to what it does. None of it is
 * read by the simulation; all of it is on the wire, because a rival's colours are the point.
 *
 * **The colours here are the palette and the floor, not the choice.** A captain's own three
 * colours live in their locker (design/AccountData.md) and travel on the wire
 * (design/Networking.md); this block says which woods are on offer, how dark a sail may go, what
 * a captain who has never chosen gets, and which devices exist.
 *
 * Stored as `"#rrggbb"` strings, the same spelling `ColorField` uses in
 * [Settings.ts](Settings.ts) and the one `<input type="color">` speaks. `packColor` there turns
 * one into the number Pixi's `tint` and the wire both want.
 */
export interface Cosmetics {
    /**
     * The hull woods on offer, **ordered light to dark** — the UI draws them as a ramp, because
     * they are one axis and should look like one. At least one; the coercion falls back to the
     * shipped palette rather than leaving an empty list for a picker to divide by.
     *
     * They are approximations, deliberately. Greyscaling the timber ramp and fitting the best
     * multiply back to the shipped hull lands at `#f08036` with an rms error of 17/255 — the
     * hull people know is *not* reproducible this way, and stopped being the target the moment a
     * captain could pick the wood. See design/Art.md §6.
     */
    woods: string[];
    /**
     * How dark a sail may be, as HSL lightness 0 to 1. Enforced in the picker so an unreachable
     * colour is never offered, and again on the arena so nobody can send a black sail by hand.
     *
     * It reads as a legibility rule — Pitch.md wants a ship read in a quarter-second, and a dark
     * sail on a dark sea is not — but it is doing a second job as well. A multiply tint preserves
     * ratios, so the darker the tint the flatter the shading it delivers: the shipped red spans 25
     * points of lightness from highlight to shadow, a multiply to navy spans 14. The floor is what
     * keeps the sail's own shape readable, not only its silhouette. One dial, two reasons —
     * design/Art.md §5.
     *
     * The artist's own navy sits at 0.353, which is what the shipped floor is calibrated against.
     */
    sailLightnessFloor: number;
    /**
     * What a captain who has never opened the Colours tab puts to sea in — all four choices, not
     * only the colours, so a shipped boat can be given a device without editing code.
     */
    defaults: {
        sailTint: string;
        hullTint: string;
        emblemTint: string;
        /** Index into `emblems`. 0 is `None`, which is what a fresh captain flies. */
        emblem: number;
    };
    /** The devices on offer. Entry 0 is always `None` — see `EmblemFitting`. */
    emblems: EmblemFitting[];
}

/**
 * The whole catalogue. One file, `config/boat.json`, because its parts are only meaningful
 * against each other — a gun's price is judged against a boat's, and both are judged against
 * how fast the milestone ladder pays a captain for holding a rank.
 */
export interface BoatTree {
    /**
     * The score thresholds at which a ship levels up, as a **curve** rather than a list — see
     * `MilestoneCurve`. It has no last level, so neither does a run.
     */
    milestones: MilestoneCurve;
    /** How big a boat is drawn at a given level. See `ScaleCurve`. */
    scale: ScaleCurve;
    /**
     * The boats on sale. At least one — preset 0 is the free starter every captain owns, and a
     * catalogue with nothing in it is an empty sea rather than a tuning state.
     */
    presets: BoatPreset[];
    /**
     * The guns on sale. At least one, for the same reason: type 0 is free, and a boat with no
     * gun cannot join the only fight the game has.
     */
    cannons: CannonType[];
    /** What a captain may choose about how the ship looks. See `Cosmetics`. */
    cosmetics: Cosmetics;
}

/** How long a sheet frame name may be. The longest in the `boats` sheet is about 44. */
export const FRAME_MAX = 96;

/**
 * How many guns a boat carries: **exactly two, bow then stern**.
 *
 * A rule about the game rather than about a hull, which is why it is a constant here and not a
 * number each preset happens to have authored. The coercion pads and trims every preset's
 * `cannonSlots` to it, so the roster, the header's battery strip and the wire array can all
 * count on two without checking.
 *
 * The deck plan used to be the interesting part of the tree — a Galleon carried eight rails and
 * *which* rail got the better gun was a real decision. It was also asked mid-battle, where
 * nobody could answer it. Two guns asks a better-shaped version of the same question ashore:
 * what kind of fighter am I, forward and aft. See design/Evolution.md.
 */
export const CANNON_SLOTS = 2;

/**
 * Ceilings on how much catalogue there can be, so a corrupt file cannot allocate without bound.
 * The milestone ladder is not among them: it is four numbers rather than a list, and a ladder
 * with no last rung is the whole point of it.
 */
export const MAX_ENTRIES = 12;
export const MAX_SLOTS = 16;
export const MAX_PARTS = 32;

/**
 * Bounds for every number in the tree, declared once so the coercion and the editor's inputs
 * cannot disagree. `step` is the granularity the dashboard's number boxes offer; values are
 * clamped to the range but never snapped to the step, exactly as in `Settings.ts`.
 */
export const BOAT_LIMITS = {
    milestoneBase: { min: 10, max: 100000, step: 10 },
    milestoneStep: { min: 10, max: 100000, step: 10 },
    milestoneGrowth: { min: 1, max: 2, step: 0.01 },
    milestoneRound: { min: 1, max: 500, step: 1 },
    // The scale curve. `min` is allowed above 1 and `max` below it — a catalogue where growing
    // makes a boat *smaller* is a legitimate thing to look at once before deciding against it.
    scale: { min: 0.25, max: 6, step: 0.05 },
    scaleLevels: { min: 1, max: MILESTONE_LEVEL_MAX, step: 1 },
    scaleCurve: { min: 0.2, max: 4, step: 0.05 },
    // Doubloons. The ceiling is far past anything the purse can pay in a session, on purpose:
    // pricing something out of reach is a thing a designer should be able to try.
    cost: { min: 0, max: 1000000, step: 50 },
    length: { min: 40, max: 900, step: 5 },
    speed: { min: 0.2, max: 3, step: 0.01 },
    turn: { min: 0.2, max: 3, step: 0.01 },
    cameraZoom: { min: 0.2, max: 2, step: 0.01 },
    size: { min: 10, max: 500, step: 5 },
    // The five absolutes. They bound a gun's `base`, and they bound what a climb of steps can
    // add up to — `cannonStatsAt` clamps every total against these same rows.
    steal: { min: 0.01, max: 0.95, step: 0.01 },
    range: { min: 50, max: 6600, step: 50 },
    projectileSpeed: { min: 20, max: 3000, step: 10 },
    cooldown: { min: 50, max: 20000, step: 50 },
    rotationSpeed: { min: 0, max: 360, step: 5 },
    // What one level may change them by. Symmetric about zero, because a level is allowed to
    // cost something, and wide enough that a short climb can still span the whole range.
    stealStep: { min: -0.2, max: 0.2, step: 0.001 },
    rangeStep: { min: -500, max: 500, step: 5 },
    projectileSpeedStep: { min: -200, max: 200, step: 2 },
    cooldownDrop: { min: -500, max: 500, step: 5 },
    rotationSpeedStep: { min: -60, max: 60, step: 0.5 },
    // Below 1 the climb converges; at 1 it is linear and only the absolutes above stop it.
    diminish: { min: 0.3, max: 1, step: 0.01 },
    partSize: { min: 5, max: 900, step: 2 },
    slotX: { min: -1.5, max: 1.5, step: 0.01 },
    slotY: { min: -1.5, max: 1.5, step: 0.01 },
    slotRotation: { min: -180, max: 180, step: 5 },
    slotScale: { min: 0.1, max: 4, step: 0.05 },
    muzzle: { min: -1.5, max: 1.5, step: 0.01 },
    // Cosmetics. The lightness floor stops short of 1: a floor of "only white" is not a tuning
    // state, it is a broken picker. Emblem placement is allowed outside the sail's box at both
    // ends, because a device deliberately hung off the foot of a sail is a look worth trying once.
    lightnessFloor: { min: 0, max: 0.9, step: 0.01 },
    emblemWidth: { min: 0.05, max: 1.5, step: 0.01 },
    emblemPlace: { min: -0.5, max: 1.5, step: 0.01 }
} as const;

/**
 * The hull length the camera rule is authored against, and the zoom it sits at there. This is
 * the starter preset's length — the scale the whole arena is built and tuned at
 * (design/Ships.md) — repeated as a literal because `BOAT_DEFAULTS` is initialised below and
 * cannot be read from here.
 *
 * **0.8 rather than 1.** A gun reaches 1000 world pixels and the arena is 12,000 across, so at
 * 1:1 a fresh boat on an ordinary window is being shot at from further away than it can see.
 * Pulling the camera back a fifth puts a gun's range roughly on the edge of the screen across
 * the beam, which is what "far enough to see his opponents" has to mean at these numbers, and
 * still draws the hull itself over 150px long.
 */
const ZOOM_REFERENCE_LENGTH = 190;
const ZOOM_AT_REFERENCE = 0.8;
/**
 * How much of a boat's extra size the camera gives back, as an exponent on the length ratio.
 *
 * 1 would be exact compensation — every boat drawn at precisely the same on-screen length,
 * whatever it is. Slightly under 1 leaves a bigger boat visibly a bigger boat, which reads as
 * character without ever costing the player the field of view a smaller one had.
 *
 * **Exported because growth uses it too.** It is the exponent the whole camera ladder is
 * authored against: `defaultCameraZoom` applies it across *presets*, and the battle applies it
 * again across the *scale* a captain has grown to (`cameraZoomOf` in `BattleScene.ts`). One
 * number for one rule — at 1 in either place a captain's own ship is drawn at exactly the same
 * size however far they have come, which is growth the player cannot see happening.
 */
export const ZOOM_COMPENSATION = 0.85;

/**
 * The camera zoom a boat of a given length wants, by the rule design/Ships.md describes: pull
 * back roughly in step with the hull, so the player's own ship stays about the same size on
 * screen and the sea around it shrinks instead.
 *
 * It is a **default**, not the rule the game runs on — every preset stores its own number and
 * the dashboard can drag it anywhere. It exists so that a boat added in the Boat panel arrives
 * framed sensibly, and so that a `config/boat.json` written before this field existed loads as
 * the ladder it would have been authored with rather than at a flat 1.
 *
 * Rounded to the hundredth, which is the step the dashboard offers.
 */
export function defaultCameraZoom(length: number): number {
    const ratio = ZOOM_REFERENCE_LENGTH / Math.max(1, Number.isFinite(length) ? length : ZOOM_REFERENCE_LENGTH);
    return num(Math.round(ZOOM_AT_REFERENCE * ratio ** ZOOM_COMPENSATION * 100) / 100, BOAT_LIMITS.cameraZoom, ZOOM_AT_REFERENCE);
}

/**
 * The catalogue the game ships with: three boats, three guns, a scale curve and a milestone
 * curve that never runs out of levels.
 *
 * **The three boats are meant to be different rather than better**, which is what
 * design/Shop.md asks a price spread to buy and what the dashboard's derived column exists to
 * check. The launch is free, quick, and carries one small sail; the cutter is quicker still and
 * handier but no bigger; the brig is slow and hard to come about in, and carries the only
 * two-sail rig in the catalogue, which makes it the fastest boat in the game **when it is
 * pointed the right way** and the worst one when it is not. Nobody has played this yet — it is
 * a first guess, to be dragged in the dashboard and re-judged, which is the whole point of the
 * numbers being here rather than in code.
 *
 * **The three guns are the same bet.** The standard gun is exactly the one the whole arena was
 * tuned around, and it is free. The long gun reaches half again as far with a much faster ball
 * and takes less when it lands; the carronade barely reaches but takes half a hold at a time
 * and swings twice as fast. The steal on the cheapest is deliberately the highest the
 * design/Battle.md floor allows to stay a gun that can finish a chase.
 *
 * The milestone curve is unchanged and reads 200, 350, 550, 800, 1100, 1500, 2000, 2700, 3500,
 * 4600, 6000 and on. The scale curve reaches its ceiling at level 10 — 4600 points, a strong
 * run — so most lives spend all of themselves growing.
 *
 * The frames are the ones in the `boats` sheet that read as a whole hull from above:
 * `hull_08` is the little open boat, `hull_09` and `hull_07` the two plain planked hulls.
 */
export const BOAT_DEFAULTS: BoatTree = {
    milestones: { base: 200, step: 150, growth: 1.28, round: 50 },
    scale: { min: 1, max: 1.9, levels: 10, curve: 0.75 },
    presets: [
        {
            // The launch: free, balanced, and exactly the boat the arena was tuned at.
            frame: "boats/hulls/hull_08",
            length: 190,
            speed: 1,
            turn: 1,
            cameraZoom: defaultCameraZoom(190),
            cost: 0,
            parts: [
                { frame: "boats/wheel", x: 0, y: 0.34, rotation: 0, size: 30 },
                { frame: "boats/crate_01", x: 0, y: -0.24, rotation: 12, size: 26 }
            ],
            cannonSlots: [
                { x: 0, y: -0.5, rotation: 0, scale: 1 },
                { x: 0, y: 0.62, rotation: 180, scale: 1 }
            ],
            sailSlots: [{ x: 0, y: 0.24, rotation: 0, scale: 0.75 }],
            rig: [{ frame: "sails/plain_small_sail", size: 70, speed: 1.11 }]
        },
        {
            // The cutter: quicker and handier than the launch, and no bigger. The boat for a
            // captain who intends to pick their fights and leave the ones they did not pick.
            frame: "boats/hulls/hull_09",
            length: 210,
            speed: 1.06,
            turn: 1.12,
            cameraZoom: defaultCameraZoom(210),
            cost: 600,
            parts: [
                { frame: "boats/wheel", x: 0, y: 0.38, rotation: 0, size: 32 },
                { frame: "boats/hatch_closed", x: 0, y: 0.06, rotation: 0, size: 40 },
                { frame: "boats/barrel_top_down", x: 0.3, y: -0.3, rotation: 0, size: 26 }
            ],
            cannonSlots: [
                { x: 0, y: -0.56, rotation: 0, scale: 1 },
                { x: 0, y: 0.7, rotation: 180, scale: 1 }
            ],
            sailSlots: [{ x: 0, y: 0.3, rotation: 0, scale: 0.9 }],
            rig: [{ frame: "sails/plain_small_sail", size: 74, speed: 1.13 }]
        },
        {
            // The brig: slow, committal, and the only two-sail rig in the catalogue — so it is
            // the fastest boat in the game running with the weather and the worst in irons.
            frame: "boats/hulls/hull_07",
            length: 285,
            speed: 0.88,
            turn: 0.8,
            cameraZoom: defaultCameraZoom(285),
            cost: 1800,
            parts: [
                { frame: "boats/wheel", x: 0, y: 0.44, rotation: 0, size: 38 },
                { frame: "boats/hatch_closed", x: 0, y: 0.2, rotation: 0, size: 48 },
                { frame: "boats/grate", x: 0, y: -0.16, rotation: 0, size: 46 },
                { frame: "boats/crate_01", x: -0.36, y: -0.46, rotation: -10, size: 32 },
                { frame: "boats/crate_03", x: 0.36, y: -0.46, rotation: 6, size: 32 },
                { frame: "boats/cannonball_pile", x: 0, y: 0.06, rotation: 0, size: 26 }
            ],
            cannonSlots: [
                { x: 0, y: -0.66, rotation: 0, scale: 1.1 },
                { x: 0, y: 0.78, rotation: 180, scale: 1.1 }
            ],
            sailSlots: [
                { x: 0, y: 0.5, rotation: 0, scale: 1.11 },
                { x: 0, y: 0.02, rotation: 0, scale: 0.95 }
            ],
            rig: [
                { frame: "sails/plain_big_sail", size: 79, speed: 1.22 },
                { frame: "sails/plain_small_sail", size: 70, speed: 1.11 }
            ]
        }
    ],
    cannons: [
        {
            // The gun the whole arena was tuned around: 1000px of reach against a 12,000px sea, a
            // ball that takes it in a little over three seconds, a shot every second, a quarter
            // turn of traverse in a second and a half — and the 40% steal that used to be one
            // global constant. Free, and deliberately still able to finish a chase.
            frame: "boats/cannon",
            size: 52,
            cost: 0,
            base: { steal: 0.4, range: 1000, projectileSpeed: 300, cooldown: 1000, rotationSpeed: 60 },
            perLevel: { stealStep: 0.006, rangeStep: 45, projectileSpeedStep: 8, cooldownDrop: 18, rotationSpeedStep: 1.5 },
            diminish: 0.93,
            muzzleX: 0,
            muzzleY: 0
        },
        {
            // The long gun: half again the reach and a ball that gets there, at the cost of a
            // slow reload, a slow traverse and a lighter touch when it lands. The gun for
            // fighting at a distance you chose.
            frame: "boats/cannon",
            size: 58,
            cost: 450,
            base: { steal: 0.3, range: 1500, projectileSpeed: 420, cooldown: 1400, rotationSpeed: 45 },
            perLevel: { stealStep: 0.005, rangeStep: 75, projectileSpeedStep: 12, cooldownDrop: 22, rotationSpeedStep: 1.2 },
            diminish: 0.93,
            muzzleX: 0,
            muzzleY: 0
        },
        {
            // The carronade: barely reaches, swings twice as fast as anything else, and takes
            // half a hold when it lands. The gun for a captain who intends to be alongside.
            frame: "boats/cannon",
            size: 64,
            cost: 1400,
            base: { steal: 0.52, range: 700, projectileSpeed: 260, cooldown: 800, rotationSpeed: 90 },
            perLevel: { stealStep: 0.009, rangeStep: 30, projectileSpeedStep: 6, cooldownDrop: 14, rotationSpeedStep: 2 },
            diminish: 0.93,
            muzzleX: 0,
            muzzleY: 0
        }
    ],
    cosmetics: {
        // Light to dark, because that is the axis and the UI draws it as one. Authored by eye
        // over the workspace's sea; the least-squares fit that reproduces today's hull is
        // `#f08036`, which sits between Teak and Oak and is a seed rather than an entry.
        woods: ["#f2c98a", "#d9944a", "#8a532c", "#4a3324"],
        // The artist's own navy is 0.353, so the floor sits just under the darkest sail anyone
        // drew on purpose. See `Cosmetics.sailLightnessFloor` for the second reason it exists.
        sailLightnessFloor: 0.35,
        // Today's boat: the red the sails are drawn in, a mid teak, the skull's own ivory, and no
        // device — a captain earns a flag by choosing one, not by turning up.
        defaults: { sailTint: "#f03f3f", hullTint: "#d9944a", emblemTint: "#faebaf", emblem: 0 },
        emblems: [
            // 0 is no device. Its placement is never read — an empty `frame` draws nothing — so it
            // carries the same neutral numbers the coercion would invent, rather than a zero width
            // the limits would clamp up to 0.05 and leave the file disagreeing with the tree.
            { frame: "", width: 0.5, cx: 0.5, cy: 0.5 },
            // Measured by `npm run import-boat` off `big_skull_sail_1.svg` and copied from the
            // emblems sheet's `placements`. On the small rig the artist drew it 11% wider; the
            // dashboard's colours page is where that is split by eye. See design/Art.md §7.
            { frame: "emblems/skull", width: 0.5679, cx: 0.4808, cy: 0.2716 }
        ]
    }
};

type Limit = { min: number; max: number };

/** A finite number, clamped into a limit. Anything unusable falls back to the default. */
function num(raw: unknown, limit: Limit, fallback: number): number {
    const value = Number(raw);
    if (!Number.isFinite(value)) return fallback;
    return Math.min(limit.max, Math.max(limit.min, value));
}

/** A trimmed string, truncated. Anything that is not a string falls back to the default. */
function str(raw: unknown, max: number, fallback: string): string {
    if (typeof raw !== "string") return fallback;
    const trimmed = raw.trim().slice(0, max);
    return trimmed.length > 0 ? trimmed : fallback;
}

/**
 * A `"#rrggbb"` colour, lower-cased. Anything else falls back to the default, so a hand-typed
 * `#F03F3F` is stored the way every other colour in the project is written rather than as a
 * second spelling of it — the same rule `coerceColors` applies in [Settings.ts](Settings.ts).
 */
function hex(raw: unknown, fallback: string): string {
    if (typeof raw !== "string") return fallback;
    const value = raw.trim().toLowerCase();
    return /^#[0-9a-f]{6}$/.test(value) ? value : fallback;
}

/** An array, capped, with each entry coerced. Anything that is not an array reads as empty. */
function list<T>(raw: unknown, max: number, each: (entry: unknown, index: number) => T): T[] {
    if (!Array.isArray(raw)) return [];
    return raw.slice(0, max).map(each);
}

function coerceMilestones(raw: unknown): MilestoneCurve {
    const source = (raw ?? {}) as Partial<Record<keyof MilestoneCurve, unknown>>;
    const shipped = BOAT_DEFAULTS.milestones;
    return {
        base: Math.round(num(source.base, BOAT_LIMITS.milestoneBase, shipped.base)),
        step: Math.round(num(source.step, BOAT_LIMITS.milestoneStep, shipped.step)),
        growth: num(source.growth, BOAT_LIMITS.milestoneGrowth, shipped.growth),
        round: Math.round(num(source.round, BOAT_LIMITS.milestoneRound, shipped.round))
    };
}

function coerceScale(raw: unknown): ScaleCurve {
    const source = (raw ?? {}) as Partial<Record<keyof ScaleCurve, unknown>>;
    const shipped = BOAT_DEFAULTS.scale;
    return {
        min: num(source.min, BOAT_LIMITS.scale, shipped.min),
        max: num(source.max, BOAT_LIMITS.scale, shipped.max),
        levels: Math.round(num(source.levels, BOAT_LIMITS.scaleLevels, shipped.levels)),
        curve: num(source.curve, BOAT_LIMITS.scaleCurve, shipped.curve)
    };
}

function coerceSlot(raw: unknown): Slot {
    const source = (raw ?? {}) as Partial<Record<keyof Slot, unknown>>;
    return {
        x: num(source.x, BOAT_LIMITS.slotX, 0),
        y: num(source.y, BOAT_LIMITS.slotY, 0),
        rotation: num(source.rotation, BOAT_LIMITS.slotRotation, 0),
        scale: num(source.scale, BOAT_LIMITS.slotScale, 1)
    };
}

function coercePart(raw: unknown): HullPart {
    const source = (raw ?? {}) as Partial<Record<keyof HullPart, unknown>>;
    return {
        frame: str(source.frame, FRAME_MAX, BOAT_DEFAULTS.presets[0].parts[0].frame),
        x: num(source.x, BOAT_LIMITS.slotX, 0),
        y: num(source.y, BOAT_LIMITS.slotY, 0),
        rotation: num(source.rotation, BOAT_LIMITS.slotRotation, 0),
        size: num(source.size, BOAT_LIMITS.partSize, 40)
    };
}

function coerceSail(raw: unknown): SailFitting {
    const source = (raw ?? {}) as Partial<Record<keyof SailFitting, unknown>>;
    const shipped = BOAT_DEFAULTS.presets[0].rig[0];
    return {
        frame: str(source.frame, FRAME_MAX, shipped.frame),
        size: num(source.size, BOAT_LIMITS.size, shipped.size),
        speed: num(source.speed, BOAT_LIMITS.speed, 1)
    };
}

/**
 * One boat, coerced — with its gun slots forced to exactly `CANNON_SLOTS`.
 *
 * The forcing is the one place this coercion does more than clamp, and it is deliberate: "every
 * boat carries a gun forward and a gun aft" is a rule about the game, not a number a preset
 * happens to hold, and everything downstream counts on it. A preset with too few slots is padded
 * from the starter's deck plan rather than left short, because a boat that spawns with one gun
 * is a boat that cannot shoot at half the sea and no message anywhere would say why.
 */
function coercePreset(raw: unknown): BoatPreset {
    const source = (raw ?? {}) as Partial<Record<keyof BoatPreset, unknown>>;
    const shipped = BOAT_DEFAULTS.presets[0];
    const length = num(source.length, BOAT_LIMITS.length, shipped.length);

    return {
        frame: str(source.frame, FRAME_MAX, shipped.frame),
        length,
        speed: num(source.speed, BOAT_LIMITS.speed, 1),
        turn: num(source.turn, BOAT_LIMITS.turn, 1),
        // A tree written before the camera was a dial has no key at all, and falls back to the
        // rule `defaultCameraZoom` describes rather than to 1 — an old file should arrive framed
        // the way a new one would be, not with a brig filling the screen.
        cameraZoom: num(source.cameraZoom, BOAT_LIMITS.cameraZoom, defaultCameraZoom(length)),
        cost: Math.round(num(source.cost, BOAT_LIMITS.cost, 0)),
        parts: list(source.parts, MAX_PARTS, coercePart),
        cannonSlots: exactlyTwoSlots(list(source.cannonSlots, MAX_SLOTS, coerceSlot)),
        sailSlots: list(source.sailSlots, MAX_SLOTS, coerceSlot),
        rig: list(source.rig, MAX_SLOTS, coerceSail)
    };
}

/** A gun slot list padded from the starter's deck plan and trimmed to `CANNON_SLOTS`. */
function exactlyTwoSlots(slots: Slot[]): Slot[] {
    const fallback = BOAT_DEFAULTS.presets[0].cannonSlots;
    const out: Slot[] = [];
    for (let i = 0; i < CANNON_SLOTS; i++) out.push(slots[i] ?? { ...fallback[i] });
    return out;
}

function coerceCannonStats(raw: unknown, shipped: CannonStats): CannonStats {
    const source = (raw ?? {}) as Partial<Record<keyof CannonStats, unknown>>;
    return {
        steal: num(source.steal, BOAT_LIMITS.steal, shipped.steal),
        range: num(source.range, BOAT_LIMITS.range, shipped.range),
        projectileSpeed: num(source.projectileSpeed, BOAT_LIMITS.projectileSpeed, shipped.projectileSpeed),
        cooldown: Math.round(num(source.cooldown, BOAT_LIMITS.cooldown, shipped.cooldown)),
        rotationSpeed: num(source.rotationSpeed, BOAT_LIMITS.rotationSpeed, shipped.rotationSpeed)
    };
}

/**
 * One level's worth of increment, coerced. Every step falls back to **zero** — a gun whose
 * increment is missing is a gun that does not improve, which is the only reading that cannot
 * quietly rebalance a catalogue somebody authored.
 */
function coerceCannonStep(raw: unknown): CannonStep {
    const source = (raw ?? {}) as Partial<Record<keyof CannonStep, unknown>>;
    return {
        stealStep: num(source.stealStep, BOAT_LIMITS.stealStep, 0),
        rangeStep: num(source.rangeStep, BOAT_LIMITS.rangeStep, 0),
        projectileSpeedStep: num(source.projectileSpeedStep, BOAT_LIMITS.projectileSpeedStep, 0),
        cooldownDrop: num(source.cooldownDrop, BOAT_LIMITS.cooldownDrop, 0),
        rotationSpeedStep: num(source.rotationSpeedStep, BOAT_LIMITS.rotationSpeedStep, 0)
    };
}

function coerceCannon(raw: unknown): CannonType {
    const source = (raw ?? {}) as Partial<Record<keyof CannonType, unknown>>;
    const shipped = BOAT_DEFAULTS.cannons[0];
    return {
        frame: str(source.frame, FRAME_MAX, shipped.frame),
        size: num(source.size, BOAT_LIMITS.size, shipped.size),
        cost: Math.round(num(source.cost, BOAT_LIMITS.cost, 0)),
        base: coerceCannonStats(source.base, shipped.base),
        perLevel: coerceCannonStep(source.perLevel),
        // A catalogue written without one climbs in a straight line, which is what a missing
        // `diminish` most obviously means. `BOAT_LIMITS` still stops it.
        diminish: num(source.diminish, BOAT_LIMITS.diminish, 1),
        // A tree written before the muzzle was authorable has neither key, and both fall back
        // to zero — the slot itself, which is exactly where those guns already fired from.
        muzzleX: num(source.muzzleX, BOAT_LIMITS.muzzle, 0),
        muzzleY: num(source.muzzleY, BOAT_LIMITS.muzzle, 0)
    };
}

/**
 * Turns anything at all into a usable `BoatTree`, the same way `coerceGeneral` does for the
 * sliders: unknown keys dropped, unusable ones replaced with a default, numbers clamped, and
 * every list capped.
 *
 * It runs on **both** ends — the editor's API before writing, the game on load — for the
 * reason design/Settings.md gives: a hand-edited or half-merged file must degrade to
 * something playable rather than putting a `NaN` where a boat's length goes.
 *
 * Three rules here that the flat panels do not need:
 *
 * - **A stored milestone *list* reads as the default curve.** `milestones` was an array of
 *   typed thresholds before it was a formula, and an old file may still hold one. Four
 *   parameters cannot be fitted to eight numbers by anything that deserves to run inside a
 *   coercion, so an array falls back to the four defaults — which pass through most of the
 *   ladder they replaced anyway.
 * - **There is always at least one preset and one gun.** A catalogue with neither is not a
 *   tuning state, it is an empty sea, so an empty list falls back to the shipped starter.
 * - **A tree written for the three ladders is not migrated.** `hulls`, `cannonBase`, `sails`
 *   and `start` are simply not read, and a file holding them loads as the shipped catalogue.
 *   That is a deliberate break rather than an oversight: a hull rung and a preset are different
 *   things — one was a step on a ladder, the other is a whole boat with a price — and any
 *   automatic reading of the old file would be inventing costs, a rig and a per-level curve that
 *   nobody authored. The old file is in git; the new one is re-authored by hand, once.
 */
function coerceEmblem(raw: unknown, index: number): EmblemFitting {
    const source = (raw ?? {}) as Partial<Record<keyof EmblemFitting, unknown>>;
    // Entry 0 is the device that is no device, whatever the file says it is. Everything that
    // resolves an emblem — the shop, the arena's clamp, the sprite — leans on that being true.
    const frame = index === 0 ? "" : str(source.frame, FRAME_MAX, "");
    return {
        frame,
        width: num(source.width, BOAT_LIMITS.emblemWidth, 0.5),
        cx: num(source.cx, BOAT_LIMITS.emblemPlace, 0.5),
        cy: num(source.cy, BOAT_LIMITS.emblemPlace, 0.5)
    };
}

/** A default emblem index that exists, or 0 — `None` is always a valid device to be flying. */
function emblemDefault(raw: unknown, count: number): number {
    const index = Math.floor(Number(raw));
    return Number.isFinite(index) && index >= 0 && index < count ? index : 0;
}

function coerceCosmetics(raw: unknown): Cosmetics {
    const source = (raw ?? {}) as Partial<Record<keyof Cosmetics, unknown>>;
    const shipped = BOAT_DEFAULTS.cosmetics;
    const defaults = (source.defaults ?? {}) as Partial<Record<keyof Cosmetics["defaults"], unknown>>;

    const woods = list(source.woods, MAX_ENTRIES, (entry) => hex(entry, shipped.woods[0]));
    const emblems = list(source.emblems, MAX_ENTRIES, coerceEmblem);

    return {
        // An empty palette is not a tuning state — a ramp with no rungs is a picker that cannot
        // be used and, for anything indexing into it, a division by zero waiting to happen.
        woods: woods.length > 0 ? woods : [...shipped.woods],
        sailLightnessFloor: num(source.sailLightnessFloor, BOAT_LIMITS.lightnessFloor, shipped.sailLightnessFloor),
        defaults: {
            sailTint: hex(defaults.sailTint, shipped.defaults.sailTint),
            hullTint: hex(defaults.hullTint, shipped.defaults.hullTint),
            emblemTint: hex(defaults.emblemTint, shipped.defaults.emblemTint),
            // Clamped against the list this same call just built, not against the shipped one: a
            // catalogue that lost a device should not leave every fresh captain flying past its end.
            emblem: emblemDefault(defaults.emblem, emblems.length > 0 ? emblems.length : shipped.emblems.length)
        },
        // At minimum `None`, so `emblem: 0` is always a valid choice and always means the same
        // thing. A file that lists devices but forgot to start with None gets one prepended
        // rather than having its first device silently blanked.
        emblems: emblems.length > 0 ? emblems : shipped.emblems.map((emblem) => ({ ...emblem }))
    };
}

export function coerceBoatTree(raw: unknown): BoatTree {
    const source = (raw ?? {}) as Partial<Record<keyof BoatTree, unknown>>;

    const presets = list(source.presets, MAX_ENTRIES, coercePreset);
    const cannons = list(source.cannons, MAX_ENTRIES, coerceCannon);

    return {
        milestones: coerceMilestones(source.milestones),
        scale: coerceScale(source.scale),
        presets: presets.length > 0 ? presets : [clonePreset(BOAT_DEFAULTS.presets[0])],
        cannons: cannons.length > 0 ? cannons : [cloneCannon(BOAT_DEFAULTS.cannons[0])],
        cosmetics: coerceCosmetics(source.cosmetics)
    };
}

/**
 * A deep copy of one preset. `structuredClone` would do it, but this file has to compile for
 * three toolchains and one deliberate line beats a lib-target argument.
 */
function clonePreset(preset: BoatPreset): BoatPreset {
    return {
        ...preset,
        parts: preset.parts.map((part) => ({ ...part })),
        cannonSlots: preset.cannonSlots.map((slot) => ({ ...slot })),
        sailSlots: preset.sailSlots.map((slot) => ({ ...slot })),
        rig: preset.rig.map((sail) => ({ ...sail }))
    };
}

/** The same, for a gun. Its two nested objects are flat, so this is shallower. */
function cloneCannon(cannon: CannonType): CannonType {
    return { ...cannon, base: { ...cannon.base }, perLevel: { ...cannon.perLevel } };
}

/** A low and a high, for a spread the dashboard draws rather than stores. */
export interface Spread {
    min: number;
    max: number;
}

/**
 * Numbers that fall out of the catalogue rather than being stored — the derived column of the
 * Boat panel, and the reason the panel is worth having over editing the JSON by hand.
 *
 * **Most of it is spreads**, and that is the point. design/Shop.md asks that a price buy
 * *character* rather than raw power, and the only way to see whether it does is to put the
 * fastest and the slowest boat in the catalogue side by side while the sliders move. A single
 * number cannot say that; two can.
 */
export interface BoatDerived {
    /** Score at which the first level is reached — the bottom rung of the curve. */
    firstMilestone: number;
    /** How many boats and how many guns are on sale. */
    presets: number;
    cannonTypes: number;
    /** Cruise speed across the catalogue, as a multiple of `boatBaseSpeed`. */
    speed: Spread;
    /** Turn rate across the catalogue, as a multiple of `TURN_RATE`. */
    turn: Spread;
    /** What a whole rig collects, as a multiple of the wind. See `rigDrive` in Fitout.ts. */
    drive: Spread;
    /** Authored length across the catalogue, at the bottom of the scale curve. */
    length: Spread;
    /** What the catalogue costs, in doubloons. The floor should be zero on both. */
    presetCost: Spread;
    cannonCost: Spread;
    /** A gun's reach at level 1, across the catalogue. */
    range: Spread;
    /** What a hit takes at level 1, across the catalogue. */
    steal: Spread;
    /** The scale a captain reaches at the top of the curve, and the score it takes to get there. */
    scaleTop: number;
    scaleTopScore: number;
    /** How long the biggest boat in the catalogue is once it has stopped growing, in pixels. */
    topLength: number;
}

export function deriveBoatTree(tree: BoatTree): BoatDerived {
    const presets = tree.presets;
    const cannons = tree.cannons;
    const scaleTop = scaleAt(tree.scale, tree.scale.levels);

    return {
        firstMilestone: milestoneScore(tree.milestones, 1),
        presets: presets.length,
        cannonTypes: cannons.length,
        speed: spread(presets.map((preset) => preset.speed)),
        turn: spread(presets.map((preset) => preset.turn)),
        drive: spread(presets.map((preset) => rigDriveOf(preset))),
        length: spread(presets.map((preset) => preset.length)),
        presetCost: spread(presets.map((preset) => preset.cost)),
        cannonCost: spread(cannons.map((cannon) => cannon.cost)),
        range: spread(cannons.map((cannon) => cannonStatsAt(cannon, 1).range)),
        steal: spread(cannons.map((cannon) => cannonStatsAt(cannon, 1).steal)),
        scaleTop,
        scaleTopScore: milestoneScore(tree.milestones, Math.max(1, tree.scale.levels)),
        topLength: Math.max(0, ...presets.map((preset) => preset.length)) * scaleTop
    };
}

/** The low and the high of a list, or a pair of zeroes for an empty one. */
function spread(values: number[]): Spread {
    if (values.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * What one preset's rig collects, for the derived column only.
 *
 * It is a **second implementation** of `rigDrive` in [Fitout.ts](Fitout.ts), which is normally
 * exactly the thing this repo refuses — and it is here because the alternative is worse: this
 * file is imported by the editor, which must not pull in `Config.ts` and read `config/boat.json`
 * off disk to draw a slider. The rule is one line long and is asserted against the real one by
 * the test suite, which is the cheapest way to keep two copies of four characters honest.
 */
function rigDriveOf(preset: BoatPreset): number {
    const worth = preset.rig.map((sail) => sail.speed).sort((a, b) => b - a);

    let drive = 1;
    for (let i = 0; i < worth.length; i++) drive += (worth[i] - 1) * Math.pow(0.5, i);

    return drive;
}
